/**
 * Registers the SafeCurves sign sprites with the map — wired into
 * `safecurvesConfig.map.onLoad`.
 *
 * Each sign standard ships two sprite sets, served from `static` under a folder
 * per jurisdiction code (see the README in `SPRITE_BASE_PATH` for how they are
 * regenerated); `jurisdictionStore` picks which jurisdiction is registered:
 *   - `sprite`  — the full-colour sign image (`SAFECURVES_SPRITE_ID`)
 *   - `outline` — an SDF sprite used to tint the sign halo (`SAFECURVES_OUTLINE_SPRITE_ID`)
 *
 * Symbol layers in `layer_styles/` reference icons namespaced by the sprite id,
 * e.g. `"icon-image": "<SAFECURVES_SPRITE_ID>:MUTCD_W1-2R"` for the image and
 * `"<SAFECURVES_OUTLINE_SPRITE_ID>:MUTCD_W1-2R"` for the halo.
 */

import { getLogger } from '@logtape/logtape';
import type { Map } from 'maplibre-gl';

import { jurisdictionStore, type Jurisdiction } from '#lib/stores/jurisdictionStore.svelte.js';

const log = getLogger(['safesystem', 'safecurves', 'sprites']);

// The `{id}:` prefixes used by `icon-image` in layer-style JSON.
const SAFECURVES_SPRITE_ID = 'safecurves';
const SAFECURVES_OUTLINE_SPRITE_ID = 'safecurves-outline';

const SPRITE_BASE_PATH = '/sprites/safesystem';

// Root-relative path of a sprite artifact for a jurisdiction, without the
// `.json`/`.png` (or `@2x`) suffixes MapLibre appends itself.
const spritePath = (jurisdiction: Jurisdiction, name: 'sprite' | 'outline') =>
	`${SPRITE_BASE_PATH}/${jurisdiction}/${name}`;

// Root-relative sheet path (no suffix) of the current jurisdiction's full-colour
// sign sprite, for cropping a single icon into the DOM (see the legend `SpriteIcon.svelte`).
export const safeCurvesSpriteSheetPath = () => spritePath(jurisdictionStore.jurisdiction, 'sprite');

// As above, for the SDF outline sheet used to tint the sign halo in the DOM.
export const safeCurvesOutlineSheetPath = () =>
	spritePath(jurisdictionStore.jurisdiction, 'outline');

export const loadSafeCurvesSprite = (map: Map) => {
	// Map.svelte only mounts once summaryData is ready, so the metadata table
	// has already set the jurisdiction by the time this runs.
	const { jurisdiction } = jurisdictionStore;
	log.debug('Registering {jurisdiction} sign sprites', { jurisdiction });
	// Sprite URLs must be absolute, hence location.origin. MapLibre fetches the
	// .json/.png (and @2x) artifacts itself, picking the right pixel ratio.
	map.addSprite(SAFECURVES_SPRITE_ID, `${location.origin}${spritePath(jurisdiction, 'sprite')}`);
	map.addSprite(
		SAFECURVES_OUTLINE_SPRITE_ID,
		`${location.origin}${spritePath(jurisdiction, 'outline')}`
	);
};
